import React, { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CreditCard, Zap, Activity, Download, ArrowUpRight, Loader2, Check,
  AlertTriangle, Receipt, Clock,
} from "lucide-react";
import { AuthContext } from "@/App";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_COLORS = {
  free: "text-gray-400", starter: "text-blue-400",
  professional: "text-[var(--qn-primary)]", enterprise: "text-amber-400",
};

export default function BillingTab() {
  const { user } = useContext(AuthContext);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchSubscription();
    // Handle Stripe redirect
    const sessionId = searchParams.get("session_id");
    const success = searchParams.get("success");
    if (success === "true" && sessionId) {
      pollCheckoutStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only checkout-status setup; searchParams/pollCheckoutStatus intentionally excluded
  }, []);

  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API}/billing/subscription`);
      setSub(res.data);
    } catch {}
    setLoading(false);
  };

  const pollCheckoutStatus = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/billing/checkout-status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        toast.success("Payment successful! Your plan has been upgraded.");
        fetchSubscription();
      }
    } catch {}
  };

  const handleUpgrade = async (plan) => {
    try {
      const res = await axios.post(`${API}/billing/create-checkout-session`,
        { plan, interval: "month" },
        { headers: { "x-origin-url": window.location.origin } }
      );
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[var(--qn-primary)]" /></div>;
  }

  const plan = sub?.plan || "free";
  const planName = sub?.plan_name || "Free";
  const usage = sub?.usage || { api_calls: 0, limit: 1000, percentage: 0 };

  return (
    <div className="space-y-6" data-testid="billing-tab">
      {/* Current Plan */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-outfit flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-[var(--qn-primary)]" />
                Current Plan
              </CardTitle>
            </div>
            <Badge className={`${PLAN_COLORS[plan]} bg-current/10 text-sm px-3 py-1`} data-testid="current-plan-badge">
              {planName}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between bg-[var(--qn-background)] rounded-lg p-4 border border-[var(--qn-border)]">
            <div>
              <div className="text-sm font-medium">Status</div>
              <div className="text-xs text-[var(--qn-foreground-muted)]">
                {sub?.current_period_end ? `Renews ${new Date(sub.current_period_end).toLocaleDateString()}` : "Active"}
              </div>
            </div>
            <Badge className="bg-emerald-500/15 text-emerald-400">{sub?.status || "Active"}</Badge>
          </div>

          {plan === "free" && (
            <div className="bg-[var(--qn-primary)]/5 border border-[var(--qn-primary)]/20 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-[var(--qn-primary)]" />
                <span className="text-sm font-medium">Upgrade for more power</span>
              </div>
              <p className="text-xs text-[var(--qn-foreground-muted)] mb-3">
                Get 10x more API calls, team features, and priority support.
              </p>
              <div className="flex gap-2">
                <Button size="sm" className="bg-[var(--qn-primary)]" onClick={() => handleUpgrade("starter")} data-testid="upgrade-starter-btn">
                  Starter $29/mo
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleUpgrade("professional")} data-testid="upgrade-pro-btn">
                  Professional $99/mo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-3">
            <Activity className="w-5 h-5 text-[var(--qn-secondary)]" />
            Usage This Month
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">API Calls</span>
              <span className="text-sm text-[var(--qn-foreground-muted)]">
                {usage.api_calls.toLocaleString()} / {usage.limit === -1 ? "Unlimited" : usage.limit.toLocaleString()}
              </span>
            </div>
            {usage.limit > 0 && (
              <div className="w-full h-3 bg-[var(--qn-background)] rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(usage.percentage, 100)}%` }}
                  className={`h-full rounded-full ${usage.percentage >= 90 ? "bg-red-500" : usage.percentage >= 75 ? "bg-amber-500" : "bg-[var(--qn-primary)]"}`}
                />
              </div>
            )}
            {usage.percentage >= 80 && plan !== "enterprise" && (
              <div className="mt-2 flex items-center gap-2 text-amber-400 text-xs">
                <AlertTriangle className="w-3.5 h-3.5" />
                {usage.percentage >= 95 ? "Critical: You're almost at your limit!" : "Approaching your monthly limit"}
              </div>
            )}
          </div>

          {sub?.resources && Object.entries(sub.resources).map(([key, val]) => (
            <div key={key} className="flex justify-between items-center py-2 border-t border-[var(--qn-border)]/50">
              <span className="text-sm capitalize">{key.replace(/_/g, " ")}</span>
              <span className="text-sm text-[var(--qn-foreground-muted)]">
                {val.used !== undefined ? `${val.used} / ${val.limit === -1 ? "Unlimited" : val.limit}` : `Limit: ${val.limit === -1 ? "Unlimited" : val.limit}`}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-3">
            <Receipt className="w-5 h-5 text-[var(--qn-foreground-muted)]" />
            Recent Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sub?.invoices?.length > 0 ? (
            <div className="space-y-2" data-testid="invoices-list">
              {sub.invoices.map((inv, i) => (
                <div key={i} className="flex items-center justify-between py-3 px-4 rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)]">
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-[var(--qn-foreground-muted)]" />
                    <div>
                      <div className="text-sm font-medium">{new Date(inv.created_at).toLocaleDateString()}</div>
                      <div className="text-xs text-[var(--qn-foreground-muted)] capitalize">{inv.plan} plan</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">${inv.amount?.toFixed(2)}</span>
                    <Badge className={inv.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}>
                      {inv.payment_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-[var(--qn-foreground-muted)]">
              No invoices yet. Upgrade to a paid plan to see billing history.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
